import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';

import {
  IconAlertCircle,
  IconCalendar,
  IconCheck,
  IconCopy,
  IconRefresh,
} from '@tabler/icons-react';

import {
  ActionIcon,
  Alert,
  Button,
  CopyButton,
  Group,
  Loader,
  Menu,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@optiro/ui';

import { useCalendarInfo, useRegenerateCalendarToken } from '@/hooks/useCalendar';

type CalendarSettingsProps = {
  accountId: string | number;
};

export function CalendarSettings({ accountId }: CalendarSettingsProps) {
  const { data: calendarInfo, isLoading } = useCalendarInfo(accountId);
  const regenerateMutation = useRegenerateCalendarToken(accountId);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);

  const handleRegenerate = () => {
    regenerateMutation.mutate(undefined, {
      onSuccess: (data) => {
        notifications.show({
          title: 'Kalender-token fornyet',
          message: data.message,
          color: 'green',
          icon: <IconCheck size={18} />,
        });
        closeConfirm();
      },
      onError: () => {
        notifications.show({
          title: 'Fejl',
          message: 'Kunne ikke forny kalender-token. Prøv igen.',
          color: 'red',
          icon: <IconAlertCircle size={18} />,
        });
      },
    });
  };

  const handleAddToCalendar = (type: 'google' | 'apple' | 'outlook') => {
    if (!calendarInfo?.calendar_url) return;

    const encodedUrl = encodeURIComponent(calendarInfo.calendar_url);

    switch (type) {
      case 'google':
        window.open(`https://calendar.google.com/calendar/r?cid=${encodedUrl}`, '_blank');
        break;
      case 'apple':
        // For Apple Calendar, just copy the URL - user needs to add it manually
        window.location.href = `webcal://${calendarInfo.calendar_url.replace(/^https?:\/\//, '')}`;
        break;
      case 'outlook':
        window.open(`https://outlook.live.com/calendar/0/addfromweb?url=${encodedUrl}`, '_blank');
        break;
    }
  };

  if (isLoading) {
    return (
      <Paper p="lg" radius="md" withBorder>
        <Group justify="center">
          <Loader size="sm" />
        </Group>
      </Paper>
    );
  }

  return (
    <Paper p="lg" radius="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Group gap="xs">
            <IconCalendar size={20} />
            <Text fw={500}>Kalender integration</Text>
          </Group>
        </Group>

        <Text size="sm" c="dimmed">
          Abonner på din Optiro kalender i din foretrukne kalender-app for at se dine bookinger
        </Text>

        {calendarInfo?.calendar_url ? (
          <>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Kalender URL
              </Text>
              <Group gap="xs">
                <Text
                  size="sm"
                  c="dimmed"
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                  }}
                >
                  {calendarInfo.calendar_url}
                </Text>
                <CopyButton value={calendarInfo.calendar_url}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Kopieret!' : 'Kopier URL'}>
                      <ActionIcon color={copied ? 'teal' : 'gray'} variant="subtle" onClick={copy}>
                        {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </Stack>

            <Group gap="sm">
              <Button
                size="sm"
                variant="light"
                leftSection={<IconCalendar size={16} />}
                onClick={() => handleAddToCalendar('google')}
              >
                Tilføj til Google Calendar
              </Button>

              <Menu position="bottom-end">
                <Menu.Target>
                  <Button size="sm" variant="subtle">
                    Andre kalendere
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={<IconCalendar size={14} />}
                    onClick={() => handleAddToCalendar('apple')}
                  >
                    Apple Calendar
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconCalendar size={14} />}
                    onClick={() => handleAddToCalendar('outlook')}
                  >
                    Outlook Calendar
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>

            {confirmOpened ? (
              <Alert color="yellow" title="Er du sikker?" icon={<IconAlertCircle size={16} />}>
                <Stack gap="sm">
                  <Text size="sm">
                    Hvis du fornyer dit kalender-token, vil alle eksisterende kalenderabonnementer
                    stoppe med at virke. Du skal opdatere dem med den nye URL.
                  </Text>
                  <Group gap="sm">
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      leftSection={<IconRefresh size={14} />}
                      onClick={handleRegenerate}
                      loading={regenerateMutation.isPending}
                    >
                      Ja, forny token
                    </Button>
                    <Button size="xs" variant="subtle" onClick={closeConfirm}>
                      Annuller
                    </Button>
                  </Group>
                </Stack>
              </Alert>
            ) : (
              <Button
                size="sm"
                variant="subtle"
                color="red"
                leftSection={<IconRefresh size={16} />}
                onClick={openConfirm}
              >
                Forny kalender-token
              </Button>
            )}
          </>
        ) : (
          <Alert color="blue" title="Generer kalender-link" icon={<IconCalendar size={16} />}>
            <Stack gap="sm">
              <Text size="sm">
                Du har endnu ikke genereret et kalender-link. Klik på knappen nedenfor for at
                oprette dit personlige kalender-link.
              </Text>
              <Button
                size="sm"
                leftSection={<IconRefresh size={16} />}
                onClick={handleRegenerate}
                loading={regenerateMutation.isPending}
                style={{ backgroundColor: '#3c735b' }}
              >
                Generer kalender-link
              </Button>
            </Stack>
          </Alert>
        )}
      </Stack>
    </Paper>
  );
}
